import React, { createContext, useContext, useEffect, useState } from 'react';
import { type Lang, detectLang } from '@/utils/i18n';

type LanguageProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Lang;
  setLanguage: (language: Lang) => void;
};

const initialState: LanguageProviderState = {
  language: 'en',
  setLanguage: () => null,
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

export function LanguageProvider({
  children,
  storageKey = 'moovatv-language',
  ...props
}: LanguageProviderProps) {
  const [language, setLanguage] = useState<Lang>(detectLang);

  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('lang', language);
  }, [language]);

  const value = {
    language,
    setLanguage: (lang: Lang) => {
      localStorage.setItem(storageKey, lang);
      setLanguage(lang);
    },
  };

  return (
    <LanguageProviderContext.Provider {...props} value={value}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);

  if (context === undefined)
    throw new Error('useLanguage must be used within a LanguageProvider');

  return context;
};
